import { inject, Injectable } from '@angular/core';
import { FieldMapping, FieldMappings } from '@models/global-forms';
import { CustomTags } from '@state/global/tags/tags.models';
import { isSet } from 'lodash';
import LogRocket from 'logrocket';
import { UtilitiesService } from './utilities.service';
import { Store } from '@ngxs/store';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { addOptionalChaining } from '@globalUtils/utilities';

@Injectable({ providedIn: 'root' })
export class GlobalFormsPdfService {
	private utils = inject(UtilitiesService);
	private store = inject(Store);

	async evaluateFields(
		fieldMappings: FieldMapping[],
		context: unknown,
	): Promise<FieldMappings> {
		const fields = {};
		for (const field of fieldMappings || []) {
			const tagValue = await this.evalExpression(
				field.fieldName,
				field.tag,
				this.store.selectSnapshot(TagsSelector.getTags),
				context,
				fields,
			);

			const value = tagValue?.defaultValue ?? tagValue;

			if (
				typeof value !== 'string' &&
				typeof value !== 'number' &&
				typeof value !== 'boolean' &&
				isSet(value)
			) {
				LogRocket.captureMessage(
					'Invalid tag value for forms: setting field to empty string',
					{ extra: { fieldName: field.fieldName, tagValue } },
				);

				fields[field.fieldName] = {
					...field,
					tagValue: '',
				} as FieldMapping;
			} else {
				fields[field.fieldName] = {
					...field,
					tagValue: value ?? '',
				} as FieldMapping;
			}
		}

		return fields;
	}

	/**
	 *
	 * @param {string} fieldName
	 * @param {string} expression
	 * @param {CustomTags} tags
	 * @param {unknown} context
	 * @param {Record<string, unknown>} fields
	 * @returns {Promise<any>} The evaluated value of the expression, MUST BE AWAITED
	 */

	async evalExpression(
		fieldName: string,
		expression: string,
		tags: CustomTags,
		context: unknown,
		fields: Record<string, unknown>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	): Promise<any> {
		if (!expression?.includes('return ')) {
			expression = 'return ' + expression;
		}
		let _expression;

		try {
			_expression = addOptionalChaining(expression);

			const globalFn = new Function('tags', 'fields', 'utils', _expression);

			// Bind the function to the given context
			const scopedFn = globalFn.bind(context);
			// THIS NEEDS TO BE AWAITED, the function expression can be async, using this.utils.decrypt
			return await scopedFn(tags, fields, this.utils);
		} catch {
			// Pull out the first line of the error message to make it more readable
			// TODO: restore log once we have a better grasp of what's failing in tests. Shouldn't be a breaking issue.
			// Logger.error(
			// 	`Failed to evaluate field expression, ${e}`,
			// );
			return null;
		}
	}
}
